if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk')?.dataset.id;
        }

        return this._sectionId;
      }

      async onCartUpdate() {
        if (this.sectionId) {
          try {
            const response = await fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`);
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
            if (sourceQty) {
              this.innerHTML = sourceQty.innerHTML;
              return;
            }
          } catch (e) {
            console.error(e);
          }
        }
        await this.refreshFromCard();
      }

      // The card as the product renders it now (templates/product.card.liquid), for a card the section's own page does
      // not hold: a live-search result, or a card on another page. Re-read so the box, its max and the stock count
      // follow the cart and the shop's stock after every change.
      async refreshFromCard() {
        const handle = this.dataset.handle;
        if (!handle) return;
        try {
          const response = await fetch(`${window.Shopify.routes.root}products/${handle}?view=card`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const sourceQty = html.querySelector('quick-add-bulk');
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;
        } catch (e) {
          console.error(e);
        }
      }

      // Shopify refused the change (for example more than the shop holds): say why on the box, then re-read the card
      // so the box shows what the cart really holds and how many are in stock.
      showRefusal(ids, message) {
        ids.forEach((id) => {
          const input = this.querySelector(`#Quantity-${id}`);
          if (!input) return;
          input.setCustomValidity(message);
          input.reportValidity();
          input.addEventListener('keypress', () => input.setCustomValidity(''), { once: true });
        });
        return this.onCartUpdate().then(() => {
          this.listenForActiveInput();
          this.listenForKeydown();
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const ids = Object.keys(items);
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            if (parsedState.errors || parsedState.status || !parsedState.sections) {
              const message = parsedState.errors || parsedState.description || parsedState.message || '';
              return this.showRefusal(ids, Array.isArray(message) ? message.join(' ') : String(message));
            }
            this.renderSections(parsedState, ids);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch((e) => {
            console.error(e);
            return this.onCartUpdate();
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          // The card's own box is this element: a live-search result card carries no section id, so its id is not
          // the one the grid's cards have, and a lookup by id would miss it.
          const sectionElement = section.id.startsWith('quick-add-bulk-') ? this : document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            sectionElement?.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            const sectionHtml = parsedState.sections[section.section];
            const fresh = sectionHtml
              ? new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(section.selector)
              : null;
            if (fresh) {
              elementToReplace.innerHTML = fresh.innerHTML;
            } else if (section.id.startsWith('quick-add-bulk-')) {
              this.refreshFromCard();
            }
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
